/**
 * Finance Dashboard Overview
 *
 * Single-pane snapshot of the business: income, expenses, debt, assets,
 * and what needs to be acted on this week. All driven by existing summary
 * endpoints — no backend changes.
 */

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DollarSign,
  AlertCircle,
  FileText,
  Plus,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  Landmark,
  CreditCard,
  Package,
  Calculator,
  Repeat,
  CalendarClock,
  PiggyBank,
} from 'lucide-react';

// ---- types matching the existing summary endpoints ----
interface InvoiceDashboardSummary {
  totalRevenue: number;
  outstandingAmount: number;
  overdueAmount: number;
  overdueCount: number;
  draftCount: number;
  totalInvoices: number;
  paidCount: number;
  monthlyRevenue: number;
}

interface RecentInvoice {
  id: string;
  invoiceNumber: number;
  accountName: string;
  businessProfileName: string;
  status: string;
  total: number;
  amountPaid: number;
  dueDate: string;
  createdAt: string;
}

interface ExpenseSummary {
  summary: { totalSpend: number; categoryCount: number; expenseCount: number; taxDeductibleTotal: number };
  categories: Array<{ categoryName: string; categorySlug: string; totalAmount: number; expenseCount: number }>;
}

interface SubscriptionOverview {
  totalMonthlyCost: number; totalAnnualCost: number; totalQuarterlyCost: number;
  totalSemiAnnualCost: number; activeCount: number; expiringCount: number; totalCount: number;
}

interface LoanOverview {
  totalCount: number; activeCount: number; totalBalance: number;
  totalMonthlyPayment: number; totalMonthlyTax: number; totalMonthlyInsurance: number; totalOriginalAmount: number;
}

interface ChargeAccountOverview {
  totalCount: number; activeCount: number; totalBalance: number; totalCreditLimit: number;
  totalDesiredPayment: number; totalMinimumPayment: number; totalMonthlyInterest: number;
}

interface AssetSummary {
  totalAssets: number; activeAssets: number;
  totalPurchaseValue: number; activePurchaseValue: number;
  totalSection179: number;
}

interface UpcomingPayment {
  id: string; name: string; lender: string | null; paymentType: string;
  dueDate: string; minimumPayment: number; paymentAmount: number;
  currentBalance: number; interestRate: number; monthlyInterest: number;
  autopayEnabled: boolean; source: string;
}

const statusVariant: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary' | 'info'> = {
  draft: 'secondary',
  sent: 'info',
  paid: 'success',
  partial: 'warning',
  overdue: 'destructive',
  cancelled: 'secondary',
  void: 'secondary',
};

function getWeekStart(): string {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day;
  const weekStart = new Date(now.getFullYear(), now.getMonth(), diff);
  return weekStart.toISOString().split('T')[0];
}

function getMonthStart(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
}

function getTodayIso(): string {
  return new Date().toISOString().split('T')[0];
}

export function FinanceOverview() {
  const navigate = useNavigate();
  const weekStart = getWeekStart();
  const monthStart = getMonthStart();
  const today = getTodayIso();

  // Parallel queries — all summary endpoints fire at once.
  const { data: invoiceData, isLoading: invoiceLoading } = useQuery({
    queryKey: ['finance-dashboard-summary'],
    queryFn: async () => {
      const result = await api.getInvoiceSummary();
      return result.data as { summary: InvoiceDashboardSummary; recentInvoices: RecentInvoice[] };
    },
  });

  // Scope expenses to current month so the "this month" cards align with each other.
  // Note: the proc still always includes subscription/loan/charge-account-linked
  // recurring rows regardless of date — that's correct for monthly cash flow
  // because they ARE this month's recurring cost.
  const { data: expenseData } = useQuery({
    queryKey: ['expenseSummary', { mtd: true, monthStart, today }],
    queryFn: () => api.getExpenseSummary({ dateFrom: monthStart, dateTo: today }),
  });

  const { data: subData } = useQuery({
    queryKey: ['subscriptionOverview'],
    queryFn: () => api.getSubscriptionOverview(),
  });

  const { data: loanData } = useQuery({
    queryKey: ['loanOverview'],
    queryFn: () => api.getLoanOverview(),
  });

  const { data: chargeData } = useQuery({
    queryKey: ['chargeAccountOverview'],
    queryFn: () => api.getChargeAccountOverview(),
  });

  const { data: assetData } = useQuery({
    queryKey: ['assetSummary'],
    queryFn: () => api.getAssetSummary(),
  });

  const { data: upcomingData } = useQuery({
    queryKey: ['upcomingPayments', { weekStart }],
    queryFn: () => api.getUpcomingPayments({ weekStart }),
  });

  const invoiceSummary = invoiceData?.summary;
  const recentInvoices = invoiceData?.recentInvoices || [];
  const expenseSummary = (expenseData?.data as ExpenseSummary | undefined)?.summary;
  const expenseCategories = (expenseData?.data as ExpenseSummary | undefined)?.categories || [];
  const subOverview = subData?.data as SubscriptionOverview | undefined;
  const loanOverview = loanData?.data as LoanOverview | undefined;
  const chargeOverview = chargeData?.data as ChargeAccountOverview | undefined;
  const assetSummary = assetData?.data as AssetSummary | undefined;
  const upcomingPayments = (upcomingData?.data || []) as UpcomingPayment[];

  // Derived values
  const monthlyRevenue = invoiceSummary?.monthlyRevenue || 0;
  const totalRevenue = invoiceSummary?.totalRevenue || 0;
  const monthlyExpenses = expenseSummary?.totalSpend || 0; // total tracked spend (recurring + one-time)
  const taxDeductible = expenseSummary?.taxDeductibleTotal || 0;

  // Cash flow estimate: revenue this month - expenses - loan principal portion
  // Loan principal = monthly payment minus interest already in expenses
  const loanMonthlyPayment = loanOverview?.totalMonthlyPayment || 0;
  const loanEscrow = (loanOverview?.totalMonthlyTax || 0) + (loanOverview?.totalMonthlyInsurance || 0);
  const loanPI = Math.max(loanMonthlyPayment - loanEscrow, 0);
  const mortgageCat = expenseCategories.find(c => c.categorySlug === 'mortgage');
  const loansCat = expenseCategories.find(c => c.categorySlug === 'loans');
  const totalInterestExpense = (mortgageCat?.totalAmount || 0) + (loansCat?.totalAmount || 0);
  const estimatedPrincipal = Math.max(loanPI - totalInterestExpense, 0);
  const netCashFlowMonth = monthlyRevenue - monthlyExpenses - estimatedPrincipal;

  // Debt totals
  const loanBalance = loanOverview?.totalBalance || 0;
  const ccBalance = chargeOverview?.totalBalance || 0;
  const totalDebt = loanBalance + ccBalance;
  const monthlyDebtService = loanMonthlyPayment + (chargeOverview?.totalDesiredPayment || 0);
  const minimumDebtService = loanMonthlyPayment + (chargeOverview?.totalMinimumPayment || 0);
  const ccUtilization = chargeOverview && chargeOverview.totalCreditLimit > 0
    ? ((chargeOverview.totalBalance / chargeOverview.totalCreditLimit) * 100)
    : 0;

  // Assets / net worth
  const assetValue = assetSummary?.activePurchaseValue || 0;
  const netWorth = assetValue - totalDebt;

  // This week action items
  const weekTotal = upcomingPayments.reduce((s, p) => s + p.paymentAmount, 0);
  const overdueCount = invoiceSummary?.overdueCount || 0;
  const overdueAmount = invoiceSummary?.overdueAmount || 0;
  const outstandingAmount = invoiceSummary?.outstandingAmount || 0;
  const unpaidInvoices = (invoiceSummary?.totalInvoices || 0) - (invoiceSummary?.paidCount || 0);

  // Recurring monthly cost mix
  const subMonthly = subOverview?.totalMonthlyCost || 0;
  const ccMonthlyInterest = chargeOverview?.totalMonthlyInterest || 0;
  const loanMonthlyInterest = totalInterestExpense; // already monthly from expense category
  const recurringTotal = subMonthly + loanMonthlyPayment + ccMonthlyInterest;

  if (invoiceLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Finance Dashboard</h1>
          <p className="text-muted-foreground">Income, expenses, debt, and assets at a glance</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/billing/statements')}>
            <FileText className="h-4 w-4 mr-2" />
            Statements
          </Button>
          <Button onClick={() => navigate('/billing/invoices/new')}>
            <Plus className="h-4 w-4 mr-2" />
            New Invoice
          </Button>
        </div>
      </div>

      {/* Hero KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10"><TrendingUp className="h-5 w-5 text-green-500" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground">Revenue (This Month)</p>
                <p className="text-2xl font-bold">{formatCurrency(monthlyRevenue)}</p>
                <p className="text-xs text-muted-foreground">YTD: {formatCurrency(totalRevenue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10"><TrendingDown className="h-5 w-5 text-red-500" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground">Expenses (This Month)</p>
                <p className="text-2xl font-bold">{formatCurrency(monthlyExpenses)}</p>
                <p className="text-xs text-muted-foreground">{formatCurrency(taxDeductible)} tax-deductible</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={netCashFlowMonth >= 0 ? 'border-green-500/30' : 'border-red-500/30'}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${netCashFlowMonth >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                <DollarSign className={`h-5 w-5 ${netCashFlowMonth >= 0 ? 'text-green-500' : 'text-red-500'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground">Net Cash Flow (Month)</p>
                <p className={`text-2xl font-bold ${netCashFlowMonth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(netCashFlowMonth)}
                </p>
                <p className="text-xs text-muted-foreground" title="Revenue minus tracked expenses and loan principal">
                  Rev − Exp − {formatCurrency(estimatedPrincipal)} principal
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10"><PiggyBank className="h-5 w-5 text-primary" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground">Net Worth</p>
                <p className={`text-2xl font-bold ${netWorth >= 0 ? '' : 'text-red-600'}`}>
                  {formatCurrency(netWorth)}
                </p>
                <p className="text-xs text-muted-foreground">
                  Assets {formatCurrency(assetValue)} − Debt {formatCurrency(totalDebt)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* This Week — action bar */}
      {(upcomingPayments.length > 0 || overdueCount > 0) && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-amber-500/15">
                  <CalendarClock className="h-6 w-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">Bills Due This Week</p>
                    <Button variant="ghost" size="sm" onClick={() => navigate('/billing/bill-pay')}>
                      Bill Pay <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                  <p className="text-2xl font-bold">{formatCurrency(weekTotal)}</p>
                  <p className="text-xs text-muted-foreground">
                    {upcomingPayments.length} payment{upcomingPayments.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              {overdueCount > 0 && (
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-red-500/15">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">Overdue Invoices</p>
                      <Button variant="ghost" size="sm" onClick={() => navigate('/billing/invoices?status=overdue')}>
                        Collect <ArrowRight className="h-3 w-3 ml-1" />
                      </Button>
                    </div>
                    <p className="text-2xl font-bold text-red-600">{formatCurrency(overdueAmount)}</p>
                    <p className="text-xs text-muted-foreground">
                      {overdueCount} invoice{overdueCount !== 1 ? 's' : ''} past due
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Two-column main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT COLUMN (2/3) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Income & Receivables */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Income & Receivables</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/billing/revenue')}>
                Revenue <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Outstanding A/R</p>
                  <p className="text-xl font-bold">{formatCurrency(outstandingAmount)}</p>
                  <p className="text-xs text-muted-foreground">{unpaidInvoices} unpaid</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Total Invoices</p>
                  <p className="text-xl font-bold">{invoiceSummary?.totalInvoices || 0}</p>
                  <p className="text-xs text-muted-foreground">
                    {invoiceSummary?.paidCount || 0} paid · {invoiceSummary?.draftCount || 0} drafts
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Overdue</p>
                  <p className={`text-xl font-bold ${overdueCount > 0 ? 'text-red-600' : ''}`}>
                    {overdueCount}
                  </p>
                  <p className="text-xs text-muted-foreground">{formatCurrency(overdueAmount)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recurring Cost Mix */}
          {recurringTotal > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Recurring Monthly Costs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {subMonthly > 0 && (
                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <div className="flex items-center gap-2">
                          <Repeat className="h-4 w-4 text-blue-500" />
                          <span>Subscriptions</span>
                        </div>
                        <span className="font-semibold">{formatCurrency(subMonthly)}/mo</span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500/60" style={{ width: `${(subMonthly / recurringTotal) * 100}%` }} />
                      </div>
                    </div>
                  )}
                  {loanMonthlyPayment > 0 && (
                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <div className="flex items-center gap-2">
                          <Landmark className="h-4 w-4 text-amber-600" />
                          <span>Loan Payments (P&I + escrow)</span>
                        </div>
                        <span className="font-semibold">{formatCurrency(loanMonthlyPayment)}/mo</span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-amber-500/60" style={{ width: `${(loanMonthlyPayment / recurringTotal) * 100}%` }} />
                      </div>
                      {loanMonthlyInterest > 0 && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Includes {formatCurrency(loanMonthlyInterest)} interest (deductible)
                        </p>
                      )}
                    </div>
                  )}
                  {ccMonthlyInterest > 0 && (
                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <div className="flex items-center gap-2">
                          <CreditCard className="h-4 w-4 text-red-500" />
                          <span>Credit Card Interest</span>
                        </div>
                        <span className="font-semibold">{formatCurrency(ccMonthlyInterest)}/mo</span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-red-500/60" style={{ width: `${(ccMonthlyInterest / recurringTotal) * 100}%` }} />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">Tax-deductible business expense</p>
                    </div>
                  )}
                </div>
                <div className="mt-4 pt-3 border-t flex items-center justify-between">
                  <span className="text-sm font-medium">Total recurring</span>
                  <span className="text-lg font-bold">{formatCurrency(recurringTotal)}/mo</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Invoices */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Recent Invoices</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/billing/invoices')}>
                View All <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {recentInvoices.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No invoices yet</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate('/billing/invoices/new')}>
                    Create your first invoice
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {recentInvoices.slice(0, 5).map((invoice) => (
                    <div
                      key={invoice.id}
                      className="flex items-center justify-between p-2.5 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => navigate(`/billing/invoices/${invoice.id}`)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="font-mono text-xs text-muted-foreground">#{invoice.invoiceNumber}</div>
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{invoice.accountName}</div>
                          <div className="text-xs text-muted-foreground truncate">{invoice.businessProfileName}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right">
                          <div className="font-medium text-sm">{formatCurrency(invoice.total)}</div>
                          <div className="text-xs text-muted-foreground">Due {formatDate(invoice.dueDate)}</div>
                        </div>
                        <Badge variant={statusVariant[invoice.status] || 'secondary'} className="text-xs">
                          {invoice.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* RIGHT COLUMN (1/3) */}
        <div className="space-y-6">
          {/* Debt Position */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Landmark className="h-4 w-4" />
                Debt Position
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <Landmark className="h-3.5 w-3.5 text-amber-600" />
                    <span>Loans</span>
                  </div>
                  <span className="font-semibold">{formatCurrency(loanBalance)}</span>
                </div>
                <p className="text-xs text-muted-foreground ml-5">
                  {loanOverview?.activeCount || 0} active · {formatCurrency(loanMonthlyPayment)}/mo
                </p>
              </div>

              <div className="space-y-2 pt-2 border-t">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <CreditCard className="h-3.5 w-3.5 text-red-500" />
                    <span>Credit Cards</span>
                  </div>
                  <span className="font-semibold">{formatCurrency(ccBalance)}</span>
                </div>
                <p className="text-xs text-muted-foreground ml-5">
                  {chargeOverview?.activeCount || 0} active · {ccUtilization.toFixed(0)}% utilization
                </p>
              </div>

              <div className="pt-2 border-t flex items-center justify-between">
                <span className="text-sm font-semibold">Total Debt</span>
                <span className="font-bold">{formatCurrency(totalDebt)}</span>
              </div>

              <div className="bg-muted/50 rounded-md p-2.5 mt-2">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Monthly debt service</span>
                  <span className="font-semibold">{formatCurrency(monthlyDebtService)}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Minimum required</span>
                  <span>{formatCurrency(minimumDebtService)}</span>
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => navigate('/billing/loans')}>
                  Loans
                </Button>
                <Button variant="outline" size="sm" className="flex-1" onClick={() => navigate('/billing/charge-accounts')}>
                  Cards
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Assets */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="h-4 w-4" />
                Assets
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/billing/assets')}>
                <ArrowRight className="h-3 w-3" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Active Assets</span>
                <span className="font-semibold">{assetSummary?.activeAssets || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Purchase Value</span>
                <span className="font-semibold">{formatCurrency(assetValue)}</span>
              </div>
              {(assetSummary?.totalSection179 || 0) > 0 && (
                <div className="bg-green-500/10 rounded-md p-2.5 border border-green-500/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <Calculator className="h-3.5 w-3.5 text-green-600" />
                      <span className="text-xs font-medium text-green-700 dark:text-green-400">Section 179 Taken</span>
                    </div>
                    <span className="text-sm font-bold text-green-700 dark:text-green-400">
                      {formatCurrency(assetSummary?.totalSection179 || 0)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Immediate deduction this tax year</p>
                </div>
              )}
              {(!assetSummary || assetSummary.activeAssets === 0) && (
                <p className="text-xs text-muted-foreground text-center py-2">
                  No assets tracked yet
                </p>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => navigate('/billing/bill-pay')}>
                <CalendarClock className="h-4 w-4 mr-2" />
                Bill Pay
              </Button>
              <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => navigate('/billing/expenses')}>
                <TrendingDown className="h-4 w-4 mr-2" />
                Expenses
              </Button>
              <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => navigate('/billing/forecast')}>
                <TrendingUp className="h-4 w-4 mr-2" />
                Forecast
              </Button>
              <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => navigate('/billing/budgets')}>
                <PiggyBank className="h-4 w-4 mr-2" />
                Budgets
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
